import { cn } from "@/lib/utils";
import { getFormationsLabel } from "@/redux/loadedContent/loadedContentSlice";
import { useTranslation } from "@repo/shared/i18n";
import { getCoinValue } from "@repo/shared/types";
import { useSelector } from "react-redux";

import { APIStatus } from "../../../api/APIStatus.type";
import { ActiveFilter } from "../../../components/features/SquadBuilder/components/FiltersBar/FiltersBar";
import {
  Options,
  PlayerActiveBarMobile,
} from "../../../components/features/SquadBuilder/components/PlayerActiveBarMobile/PlayerActiveBarMobile";
import { SelectManager } from "../../../components/features/SquadBuilder/components/SelectManager/SelectManager";
import { FiltersSummaryMobile } from "../../../components/features/SquadBuilder/components/SquadSummaryMobile/FiltersSummaryMobile";
import { SquadSummaryMobile } from "../../../components/features/SquadBuilder/components/SquadSummaryMobile/SquadStatsMobile";
import { TacticSummaryMobile } from "../../../components/features/SquadBuilder/components/SquadSummaryMobile/TacticSummaryMobile";
import { PitchButton } from "../../../components/organisms/SquadBuilder/PitchButtons/PitchButton";
import {
  closingCross,
  loadIcon,
  saveIcon,
  shareIcon,
} from "../../../designSystemIcons";
import {
  Squad,
  SquadBuilderPlayerType,
} from "../../../redux/squadBuilder/squadBuilderUtils";
import { Tactic } from "../../../redux/tactics/tacticsSlice";

type Props = {
  children: React.ReactNode;
  tactic: Tactic;
  squad: Squad;
  tempSquad: Squad;
  benchItems: SquadBuilderPlayerType[];
  shouldWrap: boolean;
  activeBar: "playerActive" | "default" | "buildSquad";
  playerActiveBar: Options;
  handlePlayerActiveBar: (option: Options) => void;
  onClickSquadSummaryMobile: () => void;
  onClickFiltersSummaryMobile: () => void;
  onClickTacticSummaryMobile: () => void;
  handleSelectManager: (id: number, type: "country" | "league") => void;
  statusImportSquad: APIStatus;
  changingPitchItemOnIndex: number;
  currentBuiltSquadsIndex: number;
  activeMenu: "chem-overview" | "default" | "tactics" | "none";
  handleClickPitchButton: (
    action: "save" | "share" | "import" | "clear",
  ) => void;
  activeFilters: ActiveFilter[];
  /** Spotlights the AI Settings tab: the panel is closed on a phone. */
  highlightFilters?: boolean;
  hasLoadedSquad: boolean;
};
export const MobileWrapper = ({
  children,
  tactic,
  squad,
  tempSquad,
  benchItems,
  shouldWrap,
  activeBar,
  playerActiveBar,
  handlePlayerActiveBar,
  onClickSquadSummaryMobile,
  onClickFiltersSummaryMobile,
  onClickTacticSummaryMobile,
  handleSelectManager,
  statusImportSquad,
  changingPitchItemOnIndex,
  currentBuiltSquadsIndex,
  activeMenu,
  handleClickPitchButton,
  activeFilters,
  highlightFilters,
  hasLoadedSquad,
}: Props) => {
  const { t } = useTranslation(["squadBuilder", "common"]);
  const formationLabel = useSelector(
    getFormationsLabel(tactic.teamTactics.formationId),
  );

  if (!shouldWrap) return <>{children}</>;
  const teamValue = squad.players.reduce(
    (acc, player) => acc + getCoinValue(player.priceInfo),
    0,
  );
  const numberOfOwnedPlayers = squad.players.filter((p) => p.owned).length;
  const canClearSquad = squad.players.find((player) => player.resourceId);
  const disabledPlayerActiveBarButtons = [];
  let potentialChosenPlayer: SquadBuilderPlayerType;

  if (changingPitchItemOnIndex > 10) {
    potentialChosenPlayer = benchItems[changingPitchItemOnIndex - 11];
  } else if (tempSquad.players[changingPitchItemOnIndex]?.resourceId) {
    potentialChosenPlayer = tempSquad.players[changingPitchItemOnIndex];
  } else {
    potentialChosenPlayer = squad.players[changingPitchItemOnIndex];
  }
  if (potentialChosenPlayer?.evolutionPath) {
    disabledPlayerActiveBarButtons.push("chemistry");
  }

  if (potentialChosenPlayer?.resourceId && changingPitchItemOnIndex <= 10) {
    disabledPlayerActiveBarButtons.push("filter");
  } else if (changingPitchItemOnIndex > 10) {
    disabledPlayerActiveBarButtons.push("filter");
  }

  return (
    <>
      <div className="flex justify-center w-full px-1 pb-1 mx-auto text-white gap-x-1 max-w-[500px]">
        <SquadSummaryMobile
          metaRating={tempSquad.metaRating || squad.metaRating}
          chemistry={
            tempSquad.chemistry.teamChemistry || squad.chemistry.teamChemistry
          }
          price={teamValue || 0}
          owned={numberOfOwnedPlayers}
          onClick={onClickSquadSummaryMobile}
          active={activeMenu === "chem-overview"}
        />
        <div className={cn("relative w-full", highlightFilters && "z-[106]")}>
          <FiltersSummaryMobile
            formation={formationLabel}
            filters={activeFilters.map((f) => f.key)}
            onClick={onClickFiltersSummaryMobile}
            active={activeMenu === "default"}
          />
          {highlightFilters && (
            <p className="absolute left-1/2 top-full z-10 mt-1.5 w-max max-w-[220px] -translate-x-1/2 text-center text-xs text-neutral-100 motion-safe:animate-hint-fade">
              {t("squadBuilder:customize.hint")}
            </p>
          )}
        </div>
        <TacticSummaryMobile
          eaCode={tactic.eaCode}
          onClick={onClickTacticSummaryMobile}
          active={activeMenu === "tactics"}
        />
      </div>
      <div
        className={cn(
          "h-[44px] flex items-center mx-auto",
          activeBar === "default" && "bg-[#28384A] border-b border-white/5",
        )}
      >
        {activeBar === "playerActive" ? (
          <PlayerActiveBarMobile
            active={playerActiveBar}
            onClick={handlePlayerActiveBar}
            disabledButtons={disabledPlayerActiveBarButtons}
          />
        ) : (
          <div className="flex justify-between w-full px-2 mx-auto text-white">
            <div className={cn(currentBuiltSquadsIndex >= 0 && "w-[123px]")}>
              <SelectManager
                onSelect={handleSelectManager}
                country={
                  tempSquad.manager.country
                    ? tempSquad.manager.country
                    : tempSquad.manager.country === null
                      ? undefined
                      : squad.manager.country
                }
                league={
                  tempSquad.manager.league
                    ? tempSquad.manager.league
                    : tempSquad.manager.league === null
                      ? undefined
                      : squad.manager.league
                }
                disabled={currentBuiltSquadsIndex >= 0}
              />
            </div>

            <div className="flex flex-row items-center gap-x-1">
              <PitchButton
                loading={false}
                onClick={() => handleClickPitchButton("save")}
                label={
                  hasLoadedSquad ? t("common:overwrite") : t("common:save")
                }
                icon={saveIcon}
              />
              <PitchButton
                loading={false}
                onClick={() => handleClickPitchButton("share")}
                label={t("common:share")}
                icon={shareIcon}
              />
              <PitchButton
                loading={statusImportSquad === "pending"}
                onClick={() =>
                  canClearSquad
                    ? handleClickPitchButton("clear")
                    : handleClickPitchButton("import")
                }
                label={
                  canClearSquad ? t("pitch.clearSquad") : t("pitch.importSquad")
                }
                icon={
                  canClearSquad ? (
                    <div className="flex items-center justify-center w-4 h-4 p-1 my-auto bg-gray-200 rounded-full touch-device:h-5 touch-device:w-5 fill-gray-875">
                      {closingCross}
                    </div>
                  ) : (
                    loadIcon
                  )
                }
              />
            </div>
          </div>
        )}
      </div>

      {children}
    </>
  );
};
